import { X, TrendingUp, Users, DollarSign, PieChart } from "lucide-react";

export const AnalyticsTeaser = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  // Fake data for "John Doe for School Board"
  const fakeData = {
    committeeName: "John Doe for School Board",
    totalDonations: 42750.00,
    totalExpenses: 38220.00,
    uniqueDonors: 187,
    topDonor: { name: "Sarah Johnson", amount: 2500.00 },
    topExpense: { name: "Campaign Media Group", amount: 8500.00 },
    donorBreakdown: [
      { range: "$500+", count: 12, total: 18750 },
      { range: "$100-$499", count: 43, total: 14200 },
      { range: "$50-$99", count: 68, total: 5800 },
      { range: "Under $50", count: 64, total: 4000 }
    ],
    expenseCategories: [
      { category: "Advertising", amount: 15200 },
      { category: "Consulting", amount: 8500 },
      { category: "Events", amount: 6800 },
      { category: "Printing", amount: 4200 },
      { category: "Other", amount: 3520 }
    ]
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem'
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '12px',
          maxWidth: '900px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
          position: 'relative'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          position: 'sticky',
          top: 0,
          backgroundColor: 'white',
          borderTopLeftRadius: '12px',
          borderTopRightRadius: '12px',
          zIndex: 1
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '700', color: '#111827' }}>
              Analytics Preview
            </h2>
            <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>
              See the power of {fakeData.committeeName} analytics
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: '#6b7280',
              padding: '0.5rem',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
            onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
          >
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '1.5rem' }}>
          {/* Top Stats */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1rem',
            marginBottom: '2rem'
          }}>
            <div style={{
              padding: '1.25rem',
              backgroundColor: '#f0f9ff',
              borderRadius: '8px',
              border: '1px solid #bfdbfe'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <DollarSign size={20} style={{ color: '#2563eb' }} />
                <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>Total Donations</span>
              </div>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#111827' }}>
                ${fakeData.totalDonations.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>

            <div style={{
              padding: '1.25rem',
              backgroundColor: '#fef3c7',
              borderRadius: '8px',
              border: '1px solid #fcd34d'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <TrendingUp size={20} style={{ color: '#d97706' }} />
                <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>Total Expenses</span>
              </div>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#111827' }}>
                ${fakeData.totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
            </div>

            <div style={{
              padding: '1.25rem',
              backgroundColor: '#f0fdf4',
              borderRadius: '8px',
              border: '1px solid #86efac'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Users size={20} style={{ color: '#16a34a' }} />
                <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: 600 }}>Unique Donors</span>
              </div>
              <div style={{ fontSize: '1.75rem', fontWeight: '700', color: '#111827' }}>
                {fakeData.uniqueDonors}
              </div>
            </div>
          </div>

          {/* Two Column Layout */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            {/* Donor Breakdown */}
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>
                Donor Breakdown
              </h3>
              <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '1rem' }}>
                {fakeData.donorBreakdown.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '0.75rem 0',
                      borderBottom: idx < fakeData.donorBreakdown.length - 1 ? '1px solid #e5e7eb' : 'none'
                    }}
                  >
                    <div>
                      <div style={{ fontWeight: '600', color: '#111827' }}>{item.range}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{item.count} donors</div>
                    </div>
                    <div style={{ fontWeight: '700', color: '#2563eb' }}>
                      ${item.total.toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#eff6ff',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}>
                <strong style={{ color: '#1e40af' }}>Top Donor:</strong>{' '}
                <span style={{ color: '#374151' }}>
                  {fakeData.topDonor.name} - ${fakeData.topDonor.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            {/* Expense Categories */}
            <div>
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '1rem', color: '#111827' }}>
                Expense Categories
              </h3>
              <div style={{ backgroundColor: '#f9fafb', borderRadius: '8px', padding: '1rem' }}>
                {fakeData.expenseCategories.map((item, idx) => (
                  <div
                    key={idx}
                    style={{
                      marginBottom: idx < fakeData.expenseCategories.length - 1 ? '1rem' : 0
                    }}
                  >
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      marginBottom: '0.5rem'
                    }}>
                      <span style={{ fontWeight: '600', color: '#111827' }}>{item.category}</span>
                      <span style={{ fontWeight: '700', color: '#d97706' }}>
                        ${item.amount.toLocaleString()}
                      </span>
                    </div>
                    <div style={{
                      width: '100%',
                      height: '8px',
                      backgroundColor: '#e5e7eb',
                      borderRadius: '4px',
                      overflow: 'hidden'
                    }}>
                      <div style={{
                        width: `${(item.amount / fakeData.totalExpenses * 100)}%`,
                        height: '100%',
                        backgroundColor: '#d97706',
                        borderRadius: '4px'
                      }} />
                    </div>
                  </div>
                ))}
              </div>
              <div style={{
                marginTop: '1rem',
                padding: '0.75rem',
                backgroundColor: '#fef3c7',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}>
                <strong style={{ color: '#92400e' }}>Largest Expense:</strong>{' '}
                <span style={{ color: '#374151' }}>
                  {fakeData.topExpense.name} - ${fakeData.topExpense.amount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                </span>
              </div>
            </div>
          </div>

          {/* Feature Highlights */}
          <div style={{
            backgroundColor: '#f9fafb',
            borderRadius: '8px',
            padding: '1.5rem',
            marginBottom: '1.5rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
              <PieChart size={20} style={{ color: '#2563eb' }} />
              <h3 style={{ fontSize: '1.1rem', fontWeight: '600', margin: 0, color: '#111827' }}>
                More Analytics Features
              </h3>
            </div>
            <ul style={{ margin: 0, paddingLeft: '1.5rem', color: '#374151', lineHeight: '1.8' }}>
              <li>Track donation trends over time with interactive charts</li>
              <li>Identify your most generous supporters and engagement patterns</li>
              <li>Monitor spending by category and vendor</li>
              <li>Export detailed reports for FEC compliance</li>
              <li>Compare performance across multiple committees</li>
            </ul>
          </div>

          {/* CTA */}
          <div style={{
            backgroundColor: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
            borderRadius: '8px',
            padding: '1.5rem',
            textAlign: 'center',
            color: 'white'
          }}>
            <h3 style={{ fontSize: '1.25rem', fontWeight: '700', marginBottom: '0.5rem' }}>
              Unlock Full Analytics
            </h3>
            <p style={{ marginBottom: '1.25rem', opacity: 0.95 }}>
              Upgrade to Pro or Enterprise to access advanced analytics for all your committees
            </p>
            <button
              onClick={() => window.location.href = '/account/pricing'}
              className="btn-primary"
              style={{
                backgroundColor: 'white',
                color: '#2563eb',
                padding: '0.75rem 2rem',
                fontSize: '1rem',
                fontWeight: '600',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                transition: 'transform 0.2s'
              }}
              onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
              onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
            >
              View Pricing Plans
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
